import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AnalyticsService } from '@app/_services/analytics.service';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { ViewSettingsStore } from '@app/_stores/view-settings.store';

@Component({
    selector: 'app-theme-dialog',
    imports: [
        CommonModule
    ],
    templateUrl: './theme-dialog.component.html',
    styleUrl: './theme-dialog.component.scss'
})

export class ThemeDialogComponent {

  readonly viewSettings = inject(ViewSettingsStore);

  themeColors: Signal<{ name: string; color: string }[]> = computed(() => {
    const themeColors: { name: string; color: string }[] = [];
    const themes = Object.keys(this.ss.themes());
    themes.forEach((theme) => {
      // Color keys are now numeric strings ('1')
      const color = this.ss.themes()[theme]['Dark']['1'];
      themeColors.push({ name: theme, color });
    });
    // Theme keys are now lowercase letters (a, b, c, etc.)
    // Sort alphabetically
    return themeColors.sort((a, b) => a.name.localeCompare(b.name));
  });
  selectedTheme: Signal<string> = this.viewSettings.themeColor;
  selectedThemeColor: Signal<string> = computed(() => {
    // Theme keys are now lowercase letters, no capitalization needed
    const selectedTheme = this.selectedTheme();
    // Defensive check: ensure theme exists, fallback to 'a' if not
    const validTheme = this.ss.themes()[selectedTheme] ? selectedTheme : 'a';
    return this.ss.themes()[validTheme]['Dark']['1'];
  });

  constructor(
    public dialogRef: MatDialogRef<ThemeDialogComponent>,
    public ss: SettingsService,
    private as: AnalyticsService,
  ) { }

  selectTheme(theme: string, event: MouseEvent) {
    event.stopPropagation();
    if (this.selectedTheme() === theme) return;
    // Update both the ViewSettingsStore and SettingsService._viewSignals
    this.viewSettings.updateSetting('themeColor', theme);
    this.ss.viewSignals()['themeColor'].set(theme);
    this.ss.updateViewSettings();

    const themeColor = this.ss.themeColor();
    const mode = this.ss.mode();
    const capitalizedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
    // Match Flutter format: 'green' stays as-is, letter keys get 'theme' prefix + uppercase
    const colorValue = themeColor === 'green'
      ? `${themeColor}${capitalizedMode}`
      : `theme${themeColor.toUpperCase()}${capitalizedMode}`;
    this.as.userPreferenceEvent('update', 'color', colorValue);
    this.closeDialog();
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
